import { format } from 'date-fns';
import locale from 'date-fns/locale/nb';
import type { ImageHotspot } from 'sanity';
import { doCrop, getOrgSize } from "src/components/images/imageFunctions";
import getDocumentDate from "src/lib/client/getDocumentDates";
import { urlForAsset } from "src/lib/client/imageUrls";
import IAuthor from "src/lib/types/Author";
import IFilmnytt from "src/lib/types/Filmnytt";
import { toPlainText } from "@portabletext/react";
import { IFilmomtale } from "src/lib/types/Filmomtale";
import theme from "src/components/themes/DefaultTheme";
import { Review, type Genre } from "@filmweb/sanity-types";
import { StreamingContent } from "src/lib/movieinfo/movieinfotypes";

//#region [Props]
type JSONLDProps = {
    document?: IFilmnytt | IFilmomtale;
    streamingContent?: StreamingContent;
};
//#endregion

//#region [Component]
export default function JSONLD({ document, streamingContent }: JSONLDProps) {
    let jsonObj: object | null = null;
    let id: string | null = null;
    if (document) {
        id = document._id;
        switch (document._type) {
            case "filmnytt":
                jsonObj = createFilmnyttJson(document);
                break;
            case "filmomtale":
                jsonObj = createFilmJson(document);
                break;
        }
    } else if (streamingContent) {
        id = streamingContent.id.toString();
        jsonObj = createStreamingJson(streamingContent);
    }
    if (jsonObj) {
        return <script
            key={`jsonld-${id}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
                __html: JSON.stringify(jsonObj)
            }}
        />;
    }

    return null;
}
//#endregion



// https://developers.google.com/search/docs/appearance/structured-data/article#article-types
function createStreamingJson(document: StreamingContent) {
    const result: any = {
        "@context": "https://schema.org",
        "@type": document.isSeries ? "TVSeries" : "Movie",
        name: document.title
    };

    if (document.actors) {
        let names: string[] | undefined = document.actors.replace(/ og /g, ',').split(",").map(n => n.trim()).filter(n => n !== "");
        if ((names?.length ?? 0) > 0) {
            result.actor = names.map(name => ({
                "@type": "Person",
                name
            }));
        }
    }

    if (document.director) {
        let names: string[] | undefined = document.director.replace(/ og /g, ',').split(",").map(n => n.trim()).filter(n => n !== "");
        if ((names?.length ?? 0) > 0) {
            result.director = names.map(name => ({
                "@type": "Person",
                name
            }));
        }
    }

    if ((document.imagesPoster?.length ?? 0) > 0) {
        result.image = document.imagesPoster!.map(poster => poster?.url);
    }

    if ((document.nationality?.length ?? 0) > 0) {
        result.countryOfOrigin = document.nationality?.split(",").map(nat => ({
            "@type": "Country",
            name: nat
        }))
    }

    if ((document.genres?.length ?? 0) > 0) {
        result.genre = document.genres!.map(g => g);
    }

    if (document.length) {
        const dur = toISODuration(document.length);
        if (dur != null) {
            result.duration = dur;
        }
    }

    return result;
}

function createFilmJson(document: IFilmomtale) {
    const result: any = {
        "@context": "https://schema.org",
        "@type": "Movie",
        name: document.title
    };

    if (document.castV2) {
        let names: string[] | undefined = document.castV2.replace(/ og /g, ',').split(",").map(n => n.trim()).filter(n => n !== "");
        if ((names?.length ?? 0) > 0) {
            result.actor = names.map(name => ({
                "@type": "Person",
                name
            }));
        }
    }

    if (document.directorV2) {
        let names: string[] | undefined = document.directorV2.replace(/ og /g, ',').split(",").map(n => n.trim()).filter(n => n !== "");
        if ((names?.length ?? 0) > 0) {
            result.director = names.map(name => ({
                "@type": "Person",
                name
            }));
        }
    }

    const image = document.mainPosterV2 ?? document.postersV2?.[0];
    if (image?.asset?._id || image?.asset?._ref) {
        const assetRef = image?.asset?._id ?? image?.asset?._ref;
        const hotspot = image?.hotspot as ImageHotspot;


        const desiredAspects = [theme.posterAspectRatio];
        const size = getOrgSize(assetRef);
        const w = size.width;
        result.image = desiredAspects.map(aspect => {
            let h = Math.round(w * aspect);
            let imgBuilder = urlForAsset(assetRef).auto("format");
            imgBuilder = imgBuilder.width(w).height(h);
            imgBuilder = doCrop(imgBuilder, hotspot);
            return imgBuilder.url();
        });
    }

    if ((document.nationality?.length ?? 0) > 0) {
        result.countryOfOrigin = document.nationality.map(nat => ({
            "@type": "Country",
            name: nat
        }))
    }

    if ((document.genres?.length ?? 0) > 0) {
        result.genre = document.genres.map(g => (g as Genre).name);
    }

    if (document.runningTime?.trim()) {
        const dur = toISODuration(document.runningTime);
        if (dur != null) {
            result.duration = dur;
        }
    }

    if (document.productionCompany) {
        result.productionCompany = {
            "@type": "Organization",
            name: document.productionCompany
        };
    }

    if (document._createdAt) {
        result.dateCreated = document._createdAt;
    }

    if ((document.reviews?.length ?? 0) > 0) {
        const avgRating = calculateAverageReview(document.reviews!);
        if (avgRating > 0) {
            result.aggregateRating = {
                "@type": "AggregateRating",
                reviewCount: document.reviews!.length,
                bestRating: 6,
                worstRating: 1,
                ratingValue: avgRating

            }
        }
    }

    return result;
}

function createFilmnyttJson(document: IFilmnytt): object {
    const result: any = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: document.title,
        publisher: {
            "@type": "Organization",
            name: "Filmweb",
            url: "https://www.filmweb.no"
        }
    };

    const image = (document.imageV2?.length ?? 0) > 0 ? document.imageV2![0] : null;
    if (image?.asset?._id || image?.asset?._ref) {
        const assetRef = image?.asset?._id ?? image?.asset?._ref;
        const hotspot = image?.hotspot as ImageHotspot;

        const desiredAspects = [16 / 9, 4 / 3, 1];
        const size = getOrgSize(assetRef);
        const w = size.width;
        result.image = desiredAspects.map(aspect => {
            let h = Math.round(w / aspect);
            let imgBuilder = urlForAsset(assetRef).auto("format");
            imgBuilder = imgBuilder.width(w).height(h);
            imgBuilder = doCrop(imgBuilder, hotspot);
            return imgBuilder.url();
        });
    }

    const firstPublishedDate = document.firstPublishedAt ?? document.createdAt ?? document._createdAt;
    const updatedDate = document.updatedAt ?? document.eceLastUpdated ?? document._updatedAt;
    const _publishedAt: Date = getDocumentDate(firstPublishedDate, "1900-01-01T00:00:00.000Z")!;
    const _updatedAt: Date | undefined = getDocumentDate(updatedDate);

    result.datePublished = format(_publishedAt, "yyyy-MM-dd'T'HH:mm:ssxxx", { locale });
    if (_updatedAt) {
        result.dateModified = format(_updatedAt, "yyyy-MM-dd'T'HH:mm:ssxxx", { locale });
    }

    const authors: any[] = [];

    if (!document.ntbId) {
        authors.push({
            "@type": "Person",
            name: ((document.author as IAuthor)?.name ?? "Redaksjonen")
        });
    }
    if (document.source && document.source !== "Filmweb") {
        authors.push({
            "@type": "Organization",
            name: document.source
        });
    }

    result.author = authors;

    let articleBody: string = "";
    if (document.ingress) {
        articleBody = toPlainText(document.ingress) + " ";
    }
    if (document.bodytext) {
        articleBody += toPlainText(document.bodytext);
    }

    if (articleBody) {
        result.articleBody = articleBody.trim();
    }

    return result;
}

function toISODuration(input: string): string | null {
    // Normalize spacing & lowercase
    const normalized = input.toLowerCase().replace(/\s+/g, " ").trim();

    let hours = 0;
    let minutes = 0;

    // Match hours like "1 t." or "1 h"
    const hoursMatch = normalized.match(/(\d+)\s*(t\.|h)/);
    if (hoursMatch) {
        hours = parseInt(hoursMatch[1], 10);
    }

    // Match minutes like "37 min."
    const minutesMatch = normalized.match(/(\d+)\s*min/);
    if (minutesMatch) {
        minutes = parseInt(minutesMatch[1], 10);
    }

    // If nothing matched, return null
    if (hours === 0 && minutes === 0) {
        return null;
    }


    // Build ISO string
    let iso = "PT";
    if (hours) iso += `${hours}H`;
    if (minutes) iso += `${minutes}M`;

    return iso;
}

function calculateAverageReview(reviews: Review[]): number {
    const result = reviews.map(r => {
        const valueNum: number = Number(r.diceValue);
        if (Number.isNaN(valueNum)) {
            if (r.diceValue === "0/0") {
                return 0;
            } else {
                return scaleToSixRange(r.diceValue) ?? 0;
            }
        }
        return valueNum;
    }).reduce((acc, currValue) => {
        if (currValue === null) {
            return acc;
        }
        return acc! + currValue;
    }, 0);
    return result / reviews.length;
}

function scaleToSixRange(input: string): number | null {
    const [xStr, yStr] = input.split("/");
    const x = parseInt(xStr, 10);
    const y = parseInt(yStr, 10);

    if (isNaN(x) || isNaN(y) || x < 1 || y <= 1) {
        return null;
    }

    // Scale x from [1, y] to [1, 6]
    return (1 + ((x - 1) * (6 - 1)) / (y - 1));

}